import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  CreditCard, Plus, Search, Filter, RefreshCw, Settings,
  ArrowUpRight, Download, Eye, MoreVertical, FileText, CheckCircle2,
  DollarSign, Package, Link as LinkIcon, RefreshCcw, Tag, Gift, SlidersHorizontal,
  Trash2, Copy, Check, ShieldCheck, ShoppingCart, Repeat, Receipt, Globe, ExternalLink,
  Monitor
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const TABS = [
  { id: "products", label: "Products" },
  { id: "invoices", label: "Invoices & Estimates" },
  { id: "documents", label: "Documents & Contracts" },
  { id: "orders", label: "Orders" },
  { id: "subscriptions", label: "Subscriptions" },
  { id: "payment-links", label: "Payment Links" },
  { id: "transactions", label: "Transactions" },
  { id: "coupons", label: "Coupons" },
  { id: "gift-cards", label: "Gift Cards" },
  { id: "settings", label: "Settings" },
  { id: "integrations", label: "Integrations" },
];

// All payment collections start empty for clean isolation across accounts
// Real data is populated via Stripe / Backend API or user creations
const INITIAL_PRODUCTS = [];
const INITIAL_INVOICES = [];
const INITIAL_DOCUMENTS = [];
const INITIAL_ORDERS = [];
const INITIAL_SUBSCRIPTIONS = [];
const INITIAL_TRANSACTIONS = [];
const INITIAL_COUPONS = [];
const INITIAL_GIFTCARDS = [];

export default function Payments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "products";
  const setActiveTab = (id) => setSearchParams({ tab: id });
  const [products, setProducts] = useState(INITIAL_PRODUCTS);
  const [invoices, setInvoices] = useState(INITIAL_INVOICES);
  const [documents, setDocuments] = useState(INITIAL_DOCUMENTS);
  const [orders, setOrders] = useState(INITIAL_ORDERS);
  const [subscriptions, setSubscriptions] = useState(INITIAL_SUBSCRIPTIONS);
  const [transactions, setTransactions] = useState(INITIAL_TRANSACTIONS);
  const [coupons, setCoupons] = useState(INITIAL_COUPONS);
  const [giftCards, setGiftCards] = useState(INITIAL_GIFTCARDS);

  const [searchTerm, setSearchTerm] = useState("");

  // Modals
  const [newProdOpen, setNewProdOpen] = useState(false);
  const [newInvOpen, setNewInvOpen] = useState(false);
  const [newDocOpen, setNewDocOpen] = useState(false);
  const [newCouponOpen, setNewCouponOpen] = useState(false);
  const [newGiftCardOpen, setNewGiftCardOpen] = useState(false);
  const [newLinkOpen, setNewLinkOpen] = useState(false);

  // Forms
  const [prodForm, setProdForm] = useState({ name: "", price: "", type: "Physical" });
  const [invForm, setInvForm] = useState({ name: "", customer: "", amount: "", status: "Sent" });
  const [docForm, setDocForm] = useState({ title: "", client: "", value: "" });
  const [couponForm, setCouponForm] = useState({ code: "", discount: "20% OFF", limit: "50" });
  const [giftForm, setGiftForm] = useState({ recipient: "", balance: "100" });
  const [linkForm, setLinkForm] = useState({ product: "Local SEO", amount: "500" });

  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    currency: "USD ($)",
    invoicePrefix: "INV-",
    taxRate: "0.0",
    emailReceipts: true,
    stripeWebhook: "https://deck.weddingsthyzfoto.com/api/webhooks/stripe"
  });

  const loadData = async () => {
    try {
      const [prodRes, invRes] = await Promise.all([
        api.get("/payments/products").catch(() => ({ data: [] })),
        api.get("/payments/invoices").catch(() => ({ data: [] })),
      ]);
      setProducts(Array.isArray(prodRes.data) ? prodRes.data : []);
      setInvoices(Array.isArray(invRes.data) ? invRes.data : []);
    } catch {
      setProducts([]);
      setInvoices([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!prodForm.name || !prodForm.price) {
      toast.error("Please enter product name and price");
      return;
    }
    const newP = {
      id: `p-${Date.now()}`,
      name: prodForm.name,
      price: parseFloat(prodForm.price) || 0,
      type: prodForm.type,
      updated: "Just now",
      color: "#EAB308",
    };
    try {
      await api.post("/payments/products", newP);
    } catch {}
    setProducts([newP, ...products]);
    setNewProdOpen(false);
    setProdForm({ name: "", price: "", type: "Physical" });
    toast.success("Product created & catalog updated");
  };

  const handleExportCsv = () => {
    const csvRows = [
      ["Product Name", "Price", "Product Type", "Updated"],
      ...products.map(p => [p.name, p.price, p.type, p.updated])
    ];
    const csvContent = "data:text/csv;charset=utf-8," + csvRows.map(e => e.map(val => `"${val}"`).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "launchdeck_products.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Products catalog exported as CSV");
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!invForm.name || !invForm.customer || !invForm.amount) {
      toast.error("Please fill in all invoice fields");
      return;
    }
    const newI = {
      id: `INV-${1000 + invoices.length + 1}`,
      name: invForm.name,
      customer: invForm.customer,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      amount: parseFloat(invForm.amount) || 0,
      status: invForm.status,
    };
    try {
      await api.post("/payments/invoices", newI);
    } catch {}
    setInvoices([newI, ...invoices]);
    setNewInvOpen(false);
    setInvForm({ name: "", customer: "", amount: "", status: "Sent" });
    toast.success("Invoice issued & saved in ledger");
  };

  const handleCreateDoc = (e) => {
    e.preventDefault();
    if (!docForm.title || !docForm.client) {
      toast.error("Title and client name are required");
      return;
    }
    const newD = {
      id: `DOC-${200 + documents.length + 1}`,
      title: docForm.title,
      client: docForm.client,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      value: parseFloat(docForm.value) || 0,
      status: "Awaiting Signature"
    };
    setDocuments([newD, ...documents]);
    setNewDocOpen(false);
    setDocForm({ title: "", client: "", value: "" });
    toast.success("Contract draft created and dispatched for signature");
  };

  const handleCreateCoupon = (e) => {
    e.preventDefault();
    if (!couponForm.code) {
      toast.error("Coupon code is required");
      return;
    }
    const newC = {
      id: `c-${Date.now()}`,
      code: couponForm.code.toUpperCase().replace(/\s+/g, ""),
      discount: couponForm.discount,
      used: 0,
      limit: parseInt(couponForm.limit) || 50,
      expiry: "Dec 31, 2026",
      status: "Active"
    };
    setCoupons([newC, ...coupons]);
    setNewCouponOpen(false);
    setCouponForm({ code: "", discount: "20% OFF", limit: "50" });
    toast.success(`Coupon code ${newC.code} activated!`);
  };

  const handleCreateGiftCard = (e) => {
    e.preventDefault();
    if (!giftForm.recipient) {
      toast.error("Recipient name is required");
      return;
    }
    const rand = Math.floor(1000 + Math.random() * 9000);
    const newG = {
      id: `gc-${Date.now()}`,
      code: `GC-${rand}-${rand + 120}`,
      recipient: giftForm.recipient,
      balance: parseFloat(giftForm.balance) || 100,
      initial: parseFloat(giftForm.balance) || 100,
      status: "Active"
    };
    setGiftCards([newG, ...giftCards]);
    setNewGiftCardOpen(false);
    setGiftForm({ recipient: "", balance: "100" });
    toast.success(`Digital gift card issued to ${newG.recipient}!`);
  };

  const handleDeleteProduct = async (pid) => {
    try {
      await api.delete(`/payments/products/${pid}`);
    } catch {}
    setProducts(products.filter(item => item.id !== pid));
    toast.info("Product removed");
  };

  const copyPayLink = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Payment link copied to clipboard");
  };

  return (
    <div className="p-6 sm:p-8 lg:p-10 space-y-6 max-w-[1600px] mx-auto w-full" data-testid="payments-page">
      {/* ══════════════════════════════════════════════════════════════
          1. TAB: PRODUCTS (Exact Match to User Screenshot 2)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "products" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Products</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Create and Manage products for your business. <span className="text-blue-600 cursor-pointer hover:underline">Learn more</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportCsv} className="text-xs">
                Import as CSV
              </Button>
              <Button variant="outline" size="sm" onClick={() => toast.success("Stripe catalog synced (4 active products)")} className="text-xs">
                Import from Stripe
              </Button>
              <Dialog open={newProdOpen} onOpenChange={setNewProdOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                    <Plus className="w-4 h-4" /> Create Product
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Product</DialogTitle>
                    <DialogDescription>Add a physical product, digital product, or service to your catalog.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateProduct} className="space-y-4 py-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase">Product Name</label>
                      <Input
                        value={prodForm.name}
                        onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })}
                        placeholder="e.g. Local SEO"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase">Price ($ USD)</label>
                        <Input
                          type="number"
                          value={prodForm.price}
                          onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })}
                          placeholder="500.00"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase">Product Type</label>
                        <select
                          value={prodForm.type}
                          onChange={(e) => setProdForm({ ...prodForm, type: e.target.value })}
                          className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                        >
                          <option value="Physical">Physical</option>
                          <option value="Digital">Digital</option>
                          <option value="Service">Service</option>
                        </select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="ghost" onClick={() => setNewProdOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 text-white font-semibold">Save Product</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3" style={{ borderColor: "var(--border)" }}>
              <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                {products.length} Products
              </div>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-8 w-48 text-xs"
                  />
                </div>
                <Button variant="outline" size="sm" className="h-8 gap-1 text-xs">
                  <Filter className="w-3.5 h-3.5" /> Filters
                </Button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3 px-4 w-10"><input type="checkbox" className="rounded" /></th>
                    <th className="py-3 px-4">Image</th>
                    <th className="py-3 px-4">Product name</th>
                    <th className="py-3 px-4">Product Type</th>
                    <th className="py-3 px-4">Updated</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <Package className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No products yet</p>
                          <p className="text-xs text-slate-400">Click <strong>+ Create Product</strong> to add your first commercial package or retainer.</p>
                        </div>
                      </td>
                    </tr>
                  ) : products.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase())).map((p) => (
                    <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4"><input type="checkbox" className="rounded" /></td>
                      <td className="py-3.5 px-4">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold bg-slate-950 border border-slate-800 text-amber-400 text-xs shadow-xs">
                          <Monitor className="w-4 h-4 text-amber-400" />
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-semibold" style={{ color: "var(--text)" }}>{p.name}</div>
                        <div className="text-xs text-slate-500">${p.price.toFixed(2)}</div>
                      </td>
                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-400 text-xs font-medium">{p.type}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{p.updated}</td>
                      <td className="py-3.5 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="w-4 h-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => copyPayLink(`https://deck.weddingsthyzfoto.com/pay/${p.id}`)}>
                              <LinkIcon className="w-3.5 h-3.5 mr-2" /> Copy Pay Link
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toast.success(`Stripe checkout generated for ${p.name}`)}>
                              <CreditCard className="w-3.5 h-3.5 mr-2" /> Instant Checkout
                            </DropdownMenuItem>
                            <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteProduct(p.id)}>
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          2. TAB: INVOICES & ESTIMATES (Exact Match to Screenshots 3 & 4)
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "invoices" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Invoices</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Create and manage all invoices generated for your business
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setActiveTab("settings")}>
                <Settings className="w-4 h-4" /> Settings
              </Button>
              <Dialog open={newInvOpen} onOpenChange={setNewInvOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                    <Plus className="w-4 h-4" /> + New
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Generate New Invoice</DialogTitle>
                    <DialogDescription>Create and issue an invoice with payment tracking.</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateInvoice} className="space-y-4 py-2">
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase">Invoice Name / Project</label>
                      <Input
                        value={invForm.name}
                        onChange={(e) => setInvForm({ ...invForm, name: e.target.value })}
                        placeholder="e.g. Commercial Shoot & Marketing Retainer"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-500 uppercase">Customer / Client</label>
                      <Input
                        value={invForm.customer}
                        onChange={(e) => setInvForm({ ...invForm, customer: e.target.value })}
                        placeholder="e.g. Acme Hospitality LLC"
                        className="mt-1"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase">Amount ($)</label>
                        <Input
                          type="number"
                          value={invForm.amount}
                          onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })}
                          placeholder="1500.00"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-500 uppercase">Status</label>
                        <select
                          value={invForm.status}
                          onChange={(e) => setInvForm({ ...invForm, status: e.target.value })}
                          className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                          style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                        >
                          <option value="Sent">Sent</option>
                          <option value="Draft">Draft</option>
                          <option value="Paid">Paid</option>
                          <option value="Due">Due</option>
                        </select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="ghost" onClick={() => setNewInvOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-blue-600 text-white font-semibold">Issue Invoice</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* 4 Metric Cards (Matching Screenshot 3 & 4) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-medium text-slate-500">
                {invoices.filter(i => i.status === "Draft").length} Invoice(s) in Draft
              </div>
              <div className="text-3xl font-bold mt-2" style={{ color: "var(--text)" }}>$0.00</div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-medium text-slate-500">
                {invoices.filter(i => i.status === "Due").length} Invoice(s) in Due
              </div>
              <div className="text-3xl font-bold mt-2" style={{ color: "var(--text)" }}>
                ${invoices.filter(i => i.status === "Due").reduce((acc, cur) => acc + cur.amount, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-medium text-slate-500">
                {invoices.filter(i => i.status === "Paid").length} Invoice(s) received
              </div>
              <div className="text-3xl font-bold mt-2 text-emerald-600">
                ${invoices.filter(i => i.status === "Paid").reduce((acc, cur) => acc + cur.amount, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </div>
            </Card>
            <Card className="p-5 border" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="text-xs font-medium text-slate-500">
                {invoices.filter(i => i.status === "Overdue").length} Invoice(s) Overdue
              </div>
              <div className="text-3xl font-bold mt-2 text-rose-600">
                ${invoices.filter(i => i.status === "Overdue").reduce((acc, cur) => acc + cur.amount, 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </div>
            </Card>
          </div>

          {/* Filter Bar (Matching Screenshot 3 & 4) */}
          <Card className="border p-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Input type="text" placeholder="Start Date  →  End Date 📅" className="h-9 w-60 text-xs" />
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9 w-52 text-xs"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-9 gap-1 text-xs">
                  <Filter className="w-3.5 h-3.5" /> Filters
                </Button>
                <Button variant="outline" size="sm" className="h-9 w-9 p-0" onClick={() => toast.success("Refreshed")}>
                  <RefreshCw className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </Card>

          {/* Invoices Table */}
          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            {(() => {
              const filteredInvs = invoices.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.customer.toLowerCase().includes(searchTerm.toLowerCase()));
              if (filteredInvs.length === 0) {
                return (
                  <div className="py-24 flex flex-col items-center justify-center text-center space-y-3">
                    <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
                      <Search className="w-5 h-5 text-blue-500 stroke-[2.5]" />
                    </div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                      No invoices to show yet
                    </div>
                  </div>
                );
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                        <th className="py-3 px-4">Invoice Name</th>
                        <th className="py-3 px-4">Invoice Number</th>
                        <th className="py-3 px-4">Customer</th>
                        <th className="py-3 px-4">Issue Date</th>
                        <th className="py-3 px-4">Amount</th>
                        <th className="py-3 px-4">Status</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                      {filteredInvs.map((inv) => (
                        <tr key={inv.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                          <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{inv.name}</td>
                          <td className="py-3.5 px-4 font-sans text-xs text-slate-500">{inv.id}</td>
                          <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300 font-medium">{inv.customer}</td>
                          <td className="py-3.5 px-4 text-slate-500 text-xs">{inv.date}</td>
                          <td className="py-3.5 px-4 font-sans font-bold" style={{ color: "var(--text)" }}>
                            ${inv.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3.5 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              inv.status === "Paid" ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400" :
                              inv.status === "Due" ? "bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400" :
                              inv.status === "Overdue" ? "bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-400" :
                              "bg-blue-100 text-blue-800 dark:bg-blue-950/60 dark:text-blue-400"
                            }`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="py-3.5 px-4 text-right">
                            <Button variant="ghost" size="sm" onClick={() => toast.success(`Invoice ${inv.id} PDF downloaded`)}>
                              <Download className="w-4 h-4 text-slate-500" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          3. TAB: DOCUMENTS & CONTRACTS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "documents" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Documents & Contracts</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Send legally binding contracts, e-signature proposals, and quotes to prospects.
              </p>
            </div>
            <Dialog open={newDocOpen} onOpenChange={setNewDocOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                  <Plus className="w-4 h-4" /> New Document
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Proposal / Agreement</DialogTitle>
                  <DialogDescription>Draft a client contract with e-signature fields.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateDoc} className="space-y-4 py-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Document Title</label>
                    <Input
                      value={docForm.title}
                      onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                      placeholder="e.g. Master Services Agreement 2026"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Client / Counterparty</label>
                    <Input
                      value={docForm.client}
                      onChange={(e) => setDocForm({ ...docForm, client: e.target.value })}
                      placeholder="e.g. Acme Hospitality LLC"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Total Contract Value ($)</label>
                    <Input
                      type="number"
                      value={docForm.value}
                      onChange={(e) => setDocForm({ ...docForm, value: e.target.value })}
                      placeholder="3500.00"
                      className="mt-1"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewDocOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Dispatch for E-Signature</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3 px-4">Document Title</th>
                    <th className="py-3 px-4">Document ID</th>
                    <th className="py-3 px-4">Client</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Contract Value</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {documents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <FileText className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No documents or contracts yet</p>
                          <p className="text-xs text-slate-400">Click <strong>+ New Document</strong> to draft your first agreement or proposal.</p>
                        </div>
                      </td>
                    </tr>
                  ) : documents.map((doc) => (
                    <tr key={doc.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{doc.title}</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-slate-500">{doc.id}</td>
                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300 font-medium">{doc.client}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{doc.date}</td>
                      <td className="py-3.5 px-4 font-sans font-bold" style={{ color: "var(--text)" }}>
                        ${doc.value.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className={`text-xs ${
                          doc.status === "Completed" ? "bg-emerald-50 text-emerald-700 border-emerald-300" :
                          "bg-amber-50 text-amber-700 border-amber-300"
                        }`}>
                          {doc.status}
                        </Badge>
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Button variant="ghost" size="sm" onClick={() => toast.success(`Viewing agreement ${doc.id}`)}>
                          <Eye className="w-4 h-4 text-slate-500" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          4. TAB: ORDERS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "orders" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Orders</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Track online purchases, customer checkouts, and fulfillment records.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => toast.success("Orders synced with Stripe checkout")}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh Orders
            </Button>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3 px-4">Order ID</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Items</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Payment Method</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {orders.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <ShoppingBag className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No orders yet</p>
                          <p className="text-xs text-slate-400">Customer checkout orders and paid packages will appear here.</p>
                        </div>
                      </td>
                    </tr>
                  ) : orders.map((ord) => (
                    <tr key={ord.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-sans font-semibold" style={{ color: "var(--text)" }}>{ord.id}</td>
                      <td className="py-3.5 px-4 text-slate-600 dark:text-slate-300 font-medium">{ord.customer}</td>
                      <td className="py-3.5 px-4 text-xs text-slate-500">{ord.items}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{ord.date}</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-slate-500">{ord.method}</td>
                      <td className="py-3.5 px-4 font-sans font-bold text-emerald-600">${ord.total.toFixed(2)}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                          {ord.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          5. TAB: SUBSCRIPTIONS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "subscriptions" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Recurring Subscriptions</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Active monthly recurring retainers, auto-renewals, and MRR.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xs text-slate-500 font-medium">Total Monthly MRR</div>
                <div className="text-xl font-black text-emerald-600">
                  ${subscriptions.reduce((acc, cur) => acc + (cur.mrr || 0), 0).toFixed(2)} / mo
                </div>
              </div>
            </div>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3 px-4">Subscription ID</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Plan Name</th>
                    <th className="py-3 px-4">Billing Cycle</th>
                    <th className="py-3 px-4">MRR</th>
                    <th className="py-3 px-4">Next Renewal</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <CreditCard className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No active subscriptions yet</p>
                          <p className="text-xs text-slate-400">Recurring client retainers and monthly memberships will show here.</p>
                        </div>
                      </td>
                    </tr>
                  ) : subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-sans font-semibold" style={{ color: "var(--text)" }}>{sub.id}</td>
                      <td className="py-3.5 px-4 font-medium" style={{ color: "var(--text)" }}>{sub.customer}</td>
                      <td className="py-3.5 px-4 text-xs font-semibold text-blue-600">{sub.plan}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{sub.interval}</td>
                      <td className="py-3.5 px-4 font-sans font-bold text-emerald-600">${sub.mrr.toFixed(2)}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{sub.nextDate}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                          {sub.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          6. TAB: PAYMENT LINKS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "payment-links" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Payment Links</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Share direct checkout URLs with customers via SMS, Email, or WhatsApp.
              </p>
            </div>
            <Dialog open={newLinkOpen} onOpenChange={setNewLinkOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                  <Plus className="w-4 h-4" /> Create Link
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Generate Instant Payment Link</DialogTitle>
                  <DialogDescription>Create a hosted checkout URL for clients.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Product or Service</label>
                    <select
                      value={linkForm.product}
                      onChange={(e) => setLinkForm({ ...linkForm, product: e.target.value })}
                      className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                      style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                    >
                      {products.map(p => <option key={p.id} value={p.name}>{p.name} (${p.price})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Custom Price ($)</label>
                    <Input
                      type="number"
                      value={linkForm.amount}
                      onChange={(e) => setLinkForm({ ...linkForm, amount: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewLinkOpen(false)}>Cancel</Button>
                    <Button type="button" className="bg-blue-600 text-white font-semibold" onClick={() => {
                      copyPayLink(`https://deck.weddingsthyzfoto.com/pay/instant-${Date.now()}`);
                      setNewLinkOpen(false);
                    }}>
                      Create & Copy Link
                    </Button>
                  </DialogFooter>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {products.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <LinkIcon className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No payment links created yet</p>
              <p className="text-xs text-slate-400">Create a product first to generate instant payment checkout links.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.map(p => (
                <Card key={p.id} className="p-5 border flex flex-col justify-between space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div>
                    <div className="flex items-center justify-between">
                      <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>{p.name} Checkout</h3>
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">Live</Badge>
                    </div>
                    <div className="text-sm font-sans text-emerald-600 mt-1 font-bold">${p.price.toFixed(2)} USD</div>
                    <div className="text-xs text-slate-400 mt-2 font-sans truncate max-w-sm bg-slate-50 dark:bg-slate-900 p-2 rounded border" style={{ borderColor: "var(--border)" }}>
                      https://deck.weddingsthyzfoto.com/pay/{p.id}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                    <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={() => copyPayLink(`https://deck.weddingsthyzfoto.com/pay/${p.id}`)}>
                      <Copy className="w-3.5 h-3.5" /> Copy Link
                    </Button>
                    <Button variant="ghost" size="sm" className="text-blue-600 text-xs shrink-0" onClick={() => window.open(`https://deck.weddingsthyzfoto.com/pay/${p.id}`, "_blank")}>
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          7. TAB: TRANSACTIONS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "transactions" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Transactions Ledger</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Real-time record of all Stripe charges, card authorizations, and merchant fees.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => toast.success("Stripe charges synced")}>
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Sync Ledger
            </Button>
          </div>

          <Card className="border overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs font-semibold text-slate-500 uppercase" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--surface) 95%, black)" }}>
                    <th className="py-3 px-4">Charge ID</th>
                    <th className="py-3 px-4">Customer</th>
                    <th className="py-3 px-4">Method</th>
                    <th className="py-3 px-4">Gross</th>
                    <th className="py-3 px-4">Fee</th>
                    <th className="py-3 px-4">Net</th>
                    <th className="py-3 px-4">Date</th>
                    <th className="py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-12 text-center text-slate-400">
                        <div className="flex flex-col items-center gap-2">
                          <DollarSign className="w-8 h-8 text-slate-300" />
                          <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No transactions yet</p>
                          <p className="text-xs text-slate-400">Live Stripe payments and card charges will be logged here.</p>
                        </div>
                      </td>
                    </tr>
                  ) : transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                      <td className="py-3.5 px-4 font-sans text-xs text-slate-500">{tx.id}</td>
                      <td className="py-3.5 px-4 font-semibold" style={{ color: "var(--text)" }}>{tx.customer}</td>
                      <td className="py-3.5 px-4 text-xs font-sans text-slate-500">{tx.method}</td>
                      <td className="py-3.5 px-4 font-sans font-bold" style={{ color: "var(--text)" }}>${tx.gross.toFixed(2)}</td>
                      <td className="py-3.5 px-4 font-sans text-xs text-rose-500">-${tx.fee.toFixed(2)}</td>
                      <td className="py-3.5 px-4 font-sans font-bold text-emerald-600">${tx.net.toFixed(2)}</td>
                      <td className="py-3.5 px-4 text-slate-500 text-xs">{tx.date}</td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                          {tx.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          8. TAB: COUPONS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "coupons" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Discount Coupons</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Create promotional discount codes for checkouts, invoices, and campaigns.
              </p>
            </div>
            <Dialog open={newCouponOpen} onOpenChange={setNewCouponOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                  <Plus className="w-4 h-4" /> Create Coupon
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Promotional Coupon</DialogTitle>
                  <DialogDescription>Define a discount code and redemptions limit.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCoupon} className="space-y-4 py-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Coupon Code</label>
                    <Input
                      value={couponForm.code}
                      onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value })}
                      placeholder="e.g. SUMMER2026"
                      className="font-sans uppercase mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Discount Rate</label>
                    <Input
                      value={couponForm.discount}
                      onChange={(e) => setCouponForm({ ...couponForm, discount: e.target.value })}
                      placeholder="e.g. 20% OFF or $50 OFF"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Max Uses Limit</label>
                    <Input
                      type="number"
                      value={couponForm.limit}
                      onChange={(e) => setCouponForm({ ...couponForm, limit: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewCouponOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Activate Coupon</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {coupons.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <Gift className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No coupons created yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ Create Coupon</strong> to setup your first discount code.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {coupons.map((c) => (
                <Card key={c.id} className="p-5 border space-y-3" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                  <div className="flex items-center justify-between">
                    <div className="font-sans text-base font-black px-2.5 py-1 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-600 border border-blue-200">
                      {c.code}
                    </div>
                    <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-300">
                      {c.status}
                    </Badge>
                  </div>
                  <div className="text-2xl font-black" style={{ color: "var(--text)" }}>{c.discount}</div>
                  <div className="text-xs text-slate-500 flex items-center justify-between pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                    <span>{c.used} / {c.limit} used</span>
                    <span>Expires: {c.expiry}</span>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          9. TAB: GIFT CARDS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "gift-cards" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Gift Cards</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Issue digital gift cards and track card balances for client referrals.
              </p>
            </div>
            <Dialog open={newGiftCardOpen} onOpenChange={setNewGiftCardOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold gap-1.5 shadow-xs">
                  <Plus className="w-4 h-4" /> Issue Gift Card
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Issue Digital Gift Card</DialogTitle>
                  <DialogDescription>Create a digital card code with pre-funded balance.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateGiftCard} className="space-y-4 py-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Recipient Name</label>
                    <Input
                      value={giftForm.recipient}
                      onChange={(e) => setGiftForm({ ...giftForm, recipient: e.target.value })}
                      placeholder="e.g. Jessica Taylor"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 uppercase">Card Amount ($)</label>
                    <Input
                      type="number"
                      value={giftForm.balance}
                      onChange={(e) => setGiftForm({ ...giftForm, balance: e.target.value })}
                      placeholder="100.00"
                      className="mt-1"
                      required
                    />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setNewGiftCardOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-blue-600 text-white font-semibold">Issue Card</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {giftCards.length === 0 ? (
            <div className="border border-dashed rounded-xl p-14 text-center bg-white dark:bg-slate-900 flex flex-col items-center gap-2">
              <Gift className="w-8 h-8 text-slate-300" />
              <p className="font-semibold text-slate-600 dark:text-slate-300 text-sm">No gift cards issued yet</p>
              <p className="text-xs text-slate-400">Click <strong>+ Issue Gift Card</strong> to create client rewards and referral incentives.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {giftCards.map((g) => (
                <Card key={g.id} className="p-6 border space-y-4 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)", borderColor: "var(--border)" }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Gift className="w-5 h-5 text-amber-400" />
                      <span className="font-bold text-sm tracking-wide">LAUNCHDECK DIGITAL GIFT</span>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">{g.status}</Badge>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400">Cardholder</div>
                    <div className="text-lg font-bold">{g.recipient}</div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-slate-700">
                    <div className="font-sans text-xs tracking-wider text-slate-300">{g.code}</div>
                    <div className="text-xl font-sans font-black text-amber-400">${g.balance.toFixed(2)}</div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          10. TAB: SETTINGS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "settings" && (
        <div className="space-y-6 max-w-4xl">
          <div>
            <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Payment Settings</h2>
            <p className="text-sm" style={{ color: "var(--text-soft)" }}>
              Configure invoice numbering, currencies, tax rates, and webhook endpoints.
            </p>
          </div>

          <Card className="p-6 border space-y-6" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Default Currency</label>
                <select
                  value={settingsForm.currency}
                  onChange={(e) => setSettingsForm({ ...settingsForm, currency: e.target.value })}
                  className="w-full mt-1 h-9 rounded-md border px-3 text-sm"
                  style={{ background: "var(--surface)", borderColor: "var(--border)" }}
                >
                  <option value="USD ($)">USD ($) - United States Dollar</option>
                  <option value="EUR (€)">EUR (€) - Euro</option>
                  <option value="GBP (£)">GBP (£) - British Pound</option>
                  <option value="CAD ($)">CAD ($) - Canadian Dollar</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 uppercase">Invoice Number Prefix</label>
                <Input
                  value={settingsForm.invoicePrefix}
                  onChange={(e) => setSettingsForm({ ...settingsForm, invoicePrefix: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase">Stripe Webhook Endpoint</label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  readOnly
                  value={settingsForm.stripeWebhook}
                  className="font-sans text-xs bg-slate-50 dark:bg-slate-900"
                />
                <Button variant="outline" size="sm" onClick={() => {
                  navigator.clipboard.writeText(settingsForm.stripeWebhook);
                  toast.success("Webhook URL copied");
                }}>
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Add this endpoint in your Stripe Dashboard &gt; Developers &gt; Webhooks.</p>
            </div>

            <div className="pt-2 border-t flex justify-end" style={{ borderColor: "var(--border)" }}>
              <Button className="bg-blue-600 text-white font-semibold" onClick={() => toast.success("Payment settings saved")}>
                Save Settings
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════
          11. TAB: INTEGRATIONS
      ══════════════════════════════════════════════════════════════ */}
      {activeTab === "integrations" && (
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold tracking-tight" style={{ color: "var(--text)" }}>Payment Gateways</h2>
              <p className="text-sm" style={{ color: "var(--text-soft)" }}>
                Connect merchant accounts to accept credit cards, Apple Pay, Google Pay, and bank ACH.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 text-purple-600 flex items-center justify-center font-bold">
                  <CreditCard className="w-5 h-5" />
                </div>
                <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-300">Live Gateway</Badge>
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Stripe Connect</h3>
                <p className="text-xs text-slate-500 mt-0.5">Collect credit cards, Apple Pay, and automated recurring billing.</p>
              </div>
              <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => window.open("https://dashboard.stripe.com", "_blank")}>
                Open Stripe Dashboard <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
            </Card>

            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 text-blue-600 flex items-center justify-center font-bold">
                  <Globe className="w-5 h-5" />
                </div>
                <Badge variant="outline" className="text-xs text-slate-400">Optional</Badge>
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>PayPal Merchant</h3>
                <p className="text-xs text-slate-500 mt-0.5">Accept PayPal wallet checkouts and Pay in 4 installments.</p>
              </div>
              <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => toast.info("Configure PayPal client ID in Settings")}>
                Configure PayPal
              </Button>
            </Card>

            <Card className="p-6 border space-y-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 flex items-center justify-center font-bold">
                  <Receipt className="w-5 h-5" />
                </div>
                <Badge variant="outline" className="text-xs text-slate-400">Optional</Badge>
              </div>
              <div>
                <h3 className="font-bold text-base" style={{ color: "var(--text)" }}>Authorize.Net</h3>
                <p className="text-xs text-slate-500 mt-0.5">Legacy payment processing and merchant gateway integration.</p>
              </div>
              <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => toast.info("Authorize.Net API credentials available")}>
                Configure Authorize.Net
              </Button>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
